import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { GoogleMap, MarkerF, useLoadScript } from '@react-google-maps/api'
import { useLocation, useNavigate } from 'react-router-dom'
import { HelpCircle, Pause, Play, X } from 'lucide-react'
import {
  formatHonoluluTime,
  formatMinutes,
  getSunPosition,
  parseMinutesFromDate,
  todayHonoluluDateString,
  toHonoluluDate
} from '../../lib/sunPosition'
import { buildRuntimeCanonicalUrl } from '../../lib/siteUrls'
import { getRuntimeSiteContext } from '../../lib/siteContext'
import { computeShadeFromElevationGrid } from '../../lib/shadeMath'
import { trackEvent } from '../../utils/analytics'
import { getInitialPlannerMapState } from './plannerInitialState'

const MAP_LIBRARIES = []
const DEFAULT_CENTER = { lat: 22.0964, lng: -159.5261 }
const DEFAULT_ZOOM = 9
const DESKTOP_MIN_ZOOM = 7
const MOBILE_MIN_ZOOM = 6
const HAWAII_BOUNDS = {
  north: 22.9,
  south: 18.6,
  west: -161.6,
  east: -154.1
}
const ISLAND_BOUNDS = {
  kauai: { north: 22.3, south: 21.8, west: -159.95, east: -159.2 },
  oahu: { north: 21.75, south: 21.2, west: -158.4, east: -157.6 },
  maui: { north: 20.95, south: 20.45, west: -156.8, east: -155.95 },
  'big-island': { north: 20.35, south: 18.9, west: -156.2, east: -154.7 }
}
const ISLAND_LABELS = {
  kauai: 'Kauaʻi',
  oahu: 'Oʻahu',
  maui: 'Maui',
  'big-island': 'Hawaiʻi'
}
const TILE_BUDGETS = {
  desktop: {
    interactive: 72,
    quality: 120
  },
  mobile: {
    interactive: 54,
    quality: 84
  }
}
const MAX_CACHE_TILES = 280
const DEM_ZOOM = 12
const MIN_DEM_ZOOM = 8
const DAY_MS = 24 * 60 * 60 * 1000
const TIME_WINDOW_START_MINUTES = 5 * 60
const TIME_WINDOW_END_MINUTES = 20 * 60
const TERRAIN_TIME_RADIUS_METERS = 50000
const TERRAIN_TIME_STEP_METERS = 250
const TERRAIN_TIME_RAY_COUNT = 180
const EARTH_RADIUS_METERS = 6371000
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const DIRECTION_RAY_LENGTH_PX = 128
const DIRECTION_RAY_WIDTH_PX = 5
const SUN_RAY_MIN_LENGTH_PX = 40
const PLAYBACK_INTERVAL_MS = 250
const PLAYBACK_SPEED_OPTIONS = [1, 2, 4]
const mapContainerStyle = {
  width: '100%',
  height: '100%'
}

const mapOptions = {
  mapId: process.env.REACT_APP_MAP_ID,
  disableDefaultUI: true,
  zoomControl: true,
  gestureHandling: 'cooperative',
  restriction: {
    latLngBounds: HAWAII_BOUNDS,
    strictBounds: false
  }
}

const shadeDrawBuffer = {
  canvas: null,
  ctx: null,
  imageData: null,
  gridSize: 0
}

function getShadeDrawBuffer(gridSize) {
  if (!shadeDrawBuffer.canvas || shadeDrawBuffer.gridSize !== gridSize) {
    const canvas = document.createElement('canvas')
    canvas.width = gridSize
    canvas.height = gridSize
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Offscreen canvas 2D context unavailable')
    shadeDrawBuffer.canvas = canvas
    shadeDrawBuffer.ctx = ctx
    shadeDrawBuffer.imageData = ctx.createImageData(gridSize, gridSize)
    shadeDrawBuffer.gridSize = gridSize
  }
  return shadeDrawBuffer
}

function decodeTerrainRgb(r, g, b) {
  return -10000 + (r * 256 * 256 + g * 256 + b) * 0.1
}

function latLngToTile(lat, lng, zoom) {
  const scale = 1 << zoom
  const x = ((lng + 180) / 360) * scale
  const sinLat = Math.sin((lat * Math.PI) / 180)
  const y = (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale
  return { x, y }
}

function haversineMeters(a, b) {
  const R = 6371000
  const toRad = Math.PI / 180
  const dLat = (b.lat - a.lat) * toRad
  const dLng = (b.lng - a.lng) * toRad
  const lat1 = a.lat * toRad
  const lat2 = b.lat * toRad
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(h))
}

function buildTileUrl({ x, y, z, key }) {
  return `https://api.maptiler.com/tiles/terrain-rgb/${z}/${x}/${y}.png?key=${key}`
}

async function loadTerrainTile({ x, y, z, key }) {
  const url = buildTileUrl({ x, y, z, key })
  const img = new Image()
  img.crossOrigin = 'anonymous'
  img.src = url
  if (img.decode) {
    await img.decode()
  } else {
    await new Promise((resolve, reject) => {
      img.onload = resolve
      img.onerror = reject
    })
  }

  const canvas = document.createElement('canvas')
  canvas.width = img.width
  canvas.height = img.height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas 2D context unavailable')
  ctx.drawImage(img, 0, 0)
  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height)

  const elevations = new Float32Array(canvas.width * canvas.height)
  for (let i = 0; i < elevations.length; i += 1) {
    const idx = i * 4
    elevations[i] = decodeTerrainRgb(data[idx], data[idx + 1], data[idx + 2])
  }

  return {
    x,
    y,
    z,
    size: canvas.width,
    elevations
  }
}

function getElevationAt(lat, lng, tileCache, zoom) {
  const { x, y } = latLngToTile(lat, lng, zoom)
  const tileX = Math.floor(x)
  const tileY = Math.floor(y)
  const tileKey = `${zoom}/${tileX}/${tileY}`
  const tile = tileCache.get(tileKey)
  if (!tile) return null

  const px = Math.min(tile.size - 1, Math.max(0, Math.floor((x - tileX) * tile.size)))
  const py = Math.min(tile.size - 1, Math.max(0, Math.floor((y - tileY) * tile.size)))
  return tile.elevations[py * tile.size + px]
}

function sampleElevationGrid({
  projection,
  width,
  height,
  originX,
  originY,
  tileCache,
  zoom,
  gridSize
}) {
  const elevations = new Float32Array(gridSize * gridSize)
  elevations.fill(Number.NaN)

  const stepX = width / gridSize
  const stepY = height / gridSize
  const centerRow = Math.floor(gridSize / 2)
  const centerCol = Math.floor(gridSize / 2)
  const eastCol = Math.min(gridSize - 1, centerCol + 1)
  const southRow = Math.min(gridSize - 1, centerRow + 1)

  let centerPoint = null
  let eastPoint = null
  let southPoint = null

  for (let row = 0; row < gridSize; row += 1) {
    for (let col = 0; col < gridSize; col += 1) {
      const px = originX + (col + 0.5) * stepX
      const py = originY + (row + 0.5) * stepY
      const latLng = projection.fromDivPixelToLatLng(new window.google.maps.Point(px, py))
      if (!latLng) continue

      const lat = latLng.lat()
      const lng = latLng.lng()
      if (row === centerRow && col === centerCol) centerPoint = { lat, lng }
      if (row === centerRow && col === eastCol) eastPoint = { lat, lng }
      if (row === southRow && col === centerCol) southPoint = { lat, lng }

      const elevation = getElevationAt(lat, lng, tileCache, zoom)
      if (!Number.isFinite(elevation)) continue
      elevations[row * gridSize + col] = elevation
    }
  }

  const dx = centerPoint && eastPoint ? haversineMeters(centerPoint, eastPoint) : 0
  const dy = centerPoint && southPoint ? haversineMeters(centerPoint, southPoint) : 0

  return {
    elevations,
    dx,
    dy
  }
}

function blurShade(shade, gridSize) {
  const out = new Uint8ClampedArray(shade.length)
  for (let row = 0; row < gridSize; row += 1) {
    for (let col = 0; col < gridSize; col += 1) {
      let sum = 0
      let count = 0
      for (let dr = -1; dr <= 1; dr += 1) {
        for (let dc = -1; dc <= 1; dc += 1) {
          const rr = row + dr
          const cc = col + dc
          if (rr < 0 || rr >= gridSize || cc < 0 || cc >= gridSize) continue
          sum += shade[rr * gridSize + cc]
          count += 1
        }
      }
      out[row * gridSize + col] = Math.round(sum / count)
    }
  }
  return out
}

function drawShade({ canvas, width, height, shade, gridSize }) {
  if (!canvas || !shade) return

  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.clearRect(0, 0, canvas.width, canvas.height)

  const buffer = getShadeDrawBuffer(gridSize)
  const offscreen = buffer.canvas
  const offCtx = buffer.ctx
  const imageData = buffer.imageData

  for (let i = 0; i < shade.length; i += 1) {
    const alpha = shade[i]
    const softenedAlpha = Math.round(alpha * 0.76)
    const idx = i * 4
    imageData.data[idx] = 8
    imageData.data[idx + 1] = 16
    imageData.data[idx + 2] = 28
    imageData.data[idx + 3] = softenedAlpha
  }

  offCtx.putImageData(imageData, 0, 0)

  ctx.imageSmoothingEnabled = true
  ctx.drawImage(offscreen, 0, 0, canvas.width, canvas.height)
}

function getTileRange(bounds, zoom) {
  const ne = bounds.getNorthEast()
  const sw = bounds.getSouthWest()

  const nwTile = latLngToTile(ne.lat(), sw.lng(), zoom)
  const seTile = latLngToTile(sw.lat(), ne.lng(), zoom)

  const minX = Math.floor(Math.min(nwTile.x, seTile.x))
  const maxX = Math.floor(Math.max(nwTile.x, seTile.x))
  const minY = Math.floor(Math.min(nwTile.y, seTile.y))
  const maxY = Math.floor(Math.max(nwTile.y, seTile.y))

  return { minX, maxX, minY, maxY }
}

function buildTileList(bounds, zoom) {
  const range = getTileRange(bounds, zoom)
  const tiles = []
  for (let x = range.minX; x <= range.maxX; x += 1) {
    for (let y = range.minY; y <= range.maxY; y += 1) {
      tiles.push({ x, y, z: zoom })
    }
  }
  return tiles
}

function pickDemZoom(bounds, mapZoom, maxTiles) {
  const zoomHint =
    typeof mapZoom === 'number'
      ? Math.min(DEM_ZOOM, Math.max(MIN_DEM_ZOOM, Math.round(mapZoom) + 2))
      : DEM_ZOOM
  for (let z = zoomHint; z >= MIN_DEM_ZOOM; z -= 1) {
    const tiles = buildTileList(bounds, z)
    if (tiles.length <= maxTiles) {
      return { zoom: z, tiles }
    }
  }
  return { zoom: MIN_DEM_ZOOM, tiles: buildTileList(bounds, MIN_DEM_ZOOM) }
}

function mergeTileCache(prev, entries) {
  const next = new Map(prev)
  entries.forEach((entry) => {
    if (!entry?.data) return
    const { key, data } = entry
    if (next.has(key)) next.delete(key)
    next.set(key, data)
  })
  while (next.size > MAX_CACHE_TILES) {
    const firstKey = next.keys().next().value
    next.delete(firstKey)
  }
  return next
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value))
}

function toIslandCenter(island) {
  if (island === 'oahu') return { lat: 21.468, lng: -157.965 }
  if (island === 'maui') return { lat: 20.7984, lng: -156.3319 }
  if (island === 'big-island') return { lat: 19.587, lng: -155.486 }
  if (island === 'kauai') return { lat: 22.052, lng: -159.5261 }
  return DEFAULT_CENTER
}

function isMobileViewport() {
  if (typeof window === 'undefined') return false
  return window.innerWidth < 768
}

function getHawaiiBoundsCenter() {
  return {
    lat: (HAWAII_BOUNDS.north + HAWAII_BOUNDS.south) / 2,
    lng: (HAWAII_BOUNDS.east + HAWAII_BOUNDS.west) / 2
  }
}

function getIslandForLatLng(lat, lng) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  return Object.entries(ISLAND_BOUNDS).find(([, bounds]) => (
    lat <= bounds.north &&
    lat >= bounds.south &&
    lng >= bounds.west &&
    lng <= bounds.east
  ))?.[0] || null
}

function getIslandZoom(island, isMobile) {
  const zooms = {
    kauai: { desktop: 11, mobile: 10 },
    oahu: { desktop: 10, mobile: 9 },
    maui: { desktop: 10, mobile: 9 },
    'big-island': { desktop: 9, mobile: 8 }
  }
  const entry = zooms[island] || { desktop: DEFAULT_ZOOM, mobile: DEFAULT_ZOOM }
  return isMobile ? entry.mobile : entry.desktop
}

function isValidLatLng(lat, lng) {
  return Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180
}

function parseDateParam(value) {
  if (!value) return null
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null
  return value
}

function parseDateParts(dateStr) {
  const parsed = parseDateParam(dateStr)
  if (!parsed) return null
  const [year, month, day] = parsed.split('-').map(Number)
  return { year, month, day }
}

function formatUtcDateToString(date) {
  const year = date.getUTCFullYear()
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date.getUTCDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function isLeapYear(year) {
  if (!Number.isFinite(year)) return false
  return year % 400 === 0 || (year % 4 === 0 && year % 100 !== 0)
}

function getDaysInYear(year) {
  return isLeapYear(year) ? 366 : 365
}

function getDayOfYear(dateStr) {
  const parts = parseDateParts(dateStr)
  if (!parts) return Number.NaN
  const start = Date.UTC(parts.year, 0, 1)
  const value = Date.UTC(parts.year, parts.month - 1, parts.day)
  return Math.floor((value - start) / DAY_MS)
}

function dayOfYearToDateString(year, dayOfYear) {
  if (!Number.isFinite(year) || !Number.isFinite(dayOfYear)) return todayHonoluluDateString()
  const clampedDay = clamp(Math.round(dayOfYear), 0, getDaysInYear(year) - 1)
  const date = new Date(Date.UTC(year, 0, 1 + clampedDay))
  return formatUtcDateToString(date)
}

function shiftDateString(dateStr, deltaDays) {
  const parts = parseDateParts(dateStr)
  if (!parts) return todayHonoluluDateString()
  const baseMs = Date.UTC(parts.year, parts.month - 1, parts.day)
  const next = new Date(baseMs + deltaDays * DAY_MS)
  return formatUtcDateToString(next)
}

function getMonthStopsForYear(year) {
  const safeYear = Number.isFinite(year) ? year : new Date().getUTCFullYear()
  const yearStart = Date.UTC(safeYear, 0, 1)
  return MONTH_LABELS.map((label, index) => ({
    label,
    dayIndex: Math.floor((Date.UTC(safeYear, index, 1) - yearStart) / DAY_MS)
  }))
}

function formatPlannerDateLabel(dateStr) {
  const parsed = parseDateParam(dateStr)
  if (!parsed) return '—'
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'Pacific/Honolulu',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(toHonoluluDate(parsed, 12 * 60))
}

function parseTimeParam(value) {
  if (!value) return null
  if (/^\d{1,2}:\d{2}$/.test(value)) {
    const [h, m] = value.split(':').map(Number)
    if (Number.isFinite(h) && Number.isFinite(m)) return (h % 24) * 60 + Math.min(59, Math.max(0, m))
  }
  const asNumber = Number(value)
  if (Number.isFinite(asNumber)) return Math.min(1439, Math.max(0, Math.round(asNumber)))
  return null
}

function clampTimeToPlannerWindow(minutes) {
  if (!Number.isFinite(minutes)) return TIME_WINDOW_START_MINUTES
  return clamp(Math.round(minutes), TIME_WINDOW_START_MINUTES, TIME_WINDOW_END_MINUTES)
}

function advanceTimeInPlannerWindow(currentMinutes, stepMinutes) {
  const normalized = clampTimeToPlannerWindow(currentMinutes)
  const step = Math.max(1, Math.round(stepMinutes))
  const range = TIME_WINDOW_END_MINUTES - TIME_WINDOW_START_MINUTES + 1
  const offset = normalized - TIME_WINDOW_START_MINUTES
  return TIME_WINDOW_START_MINUTES + ((offset + step) % range)
}

function formatMinutes24(minutes) {
  const hours = Math.floor(minutes / 60) % 24
  const mins = String(minutes % 60).padStart(2, '0')
  return `${String(hours).padStart(2, '0')}:${mins}`
}

function wrapMinutes(minutes) {
  if (!Number.isFinite(minutes)) return null
  return ((minutes % 1440) + 1440) % 1440
}

function formatMinutesRounded(minutes) {
  const wrapped = wrapMinutes(Math.round(minutes))
  return wrapped === null ? '—' : formatMinutes(wrapped)
}

function formatLatLng(lat, lng) {
  return `${lat.toFixed(4)}, ${lng.toFixed(4)}`
}

function SunLineLegend({ className = '' }) {
  return (
    <div className={className}>
      <div className="font-semibold text-white">Sun lines</div>
      <div className="mt-2 space-y-1.5">
        <div className="flex items-center gap-2">
          <span className="h-3 w-1 rounded-full bg-yellow-400 shadow-[0_0_8px_rgba(245,158,11,0.95)]" />
          <span>Yellow: current sun direction</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="h-3 w-1 rounded-full bg-orange-500 shadow-[0_0_8px_rgba(251,146,60,0.9)]" />
          <span>Orange: sunrise direction</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="h-3 w-1 rounded-full bg-red-700 shadow-[0_0_8px_rgba(239,68,68,0.85)]" />
          <span>Red: sunset direction</span>
        </div>
        <div className="max-w-[15rem] pt-1 text-[11px] leading-snug text-white/55">
          A shorter yellow line means the sun is higher overhead; a longer line means it is closer to the horizon.
        </div>
      </div>
    </div>
  )
}

function getTerrainProfileKey(point, zoom) {
  return `${zoom}:${point.lat.toFixed(4)}:${point.lng.toFixed(4)}`
}

function formatDeltaMinutes(delta) {
  if (!Number.isFinite(delta)) return '—'
  const rounded = Math.round(delta)
  const abs = Math.abs(rounded)
  if (abs === 0) return 'No difference'
  return rounded > 0 ? `${abs} min later` : `${abs} min earlier`
}

function getCrossingMinute(prevMinute, nextMinute, prevAlt, nextAlt, threshold) {
  const delta = nextAlt - prevAlt
  if (Math.abs(delta) < 1e-6) return nextMinute
  const ratio = clamp((threshold - prevAlt) / delta, 0, 1)
  return prevMinute + (nextMinute - prevMinute) * ratio
}

function findThresholdCrossings(altitudes, threshold) {
  let rise = null
  let set = null

  for (let minute = 1; minute < altitudes.length; minute += 1) {
    const prevAlt = altitudes[minute - 1]
    const nextAlt = altitudes[minute]

    if (rise === null && prevAlt < threshold && nextAlt >= threshold) {
      rise = getCrossingMinute(minute - 1, minute, prevAlt, nextAlt, threshold)
      continue
    }
    if (set === null && prevAlt >= threshold && nextAlt < threshold) {
      set = getCrossingMinute(minute - 1, minute, prevAlt, nextAlt, threshold)
    }
  }

  return { rise, set }
}

function minuteToPercent(minutes) {
  if (!Number.isFinite(minutes)) return 0
  const clamped = clampTimeToPlannerWindow(wrapMinutes(minutes))
  const range = TIME_WINDOW_END_MINUTES - TIME_WINDOW_START_MINUTES
  if (range <= 0) return 0
  return clamp(((clamped - TIME_WINDOW_START_MINUTES) / range) * 100, 0, 100)
}

function clipRangeToPlannerWindow(start, end) {
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null
  const clippedStart = Math.max(start, TIME_WINDOW_START_MINUTES)
  const clippedEnd = Math.min(end, TIME_WINDOW_END_MINUTES)
  if (clippedEnd <= clippedStart) return null
  return { start: clippedStart, end: clippedEnd }
}

function getSunRayLengthPx(altitudeDeg) {
  const normalizedAltitude = clamp((altitudeDeg + 2) / 88, 0, 1)
  const lowSunFactor = 1 - normalizedAltitude
  return Math.round(SUN_RAY_MIN_LENGTH_PX + lowSunFactor * (DIRECTION_RAY_LENGTH_PX - SUN_RAY_MIN_LENGTH_PX))
}

function normalizeAzimuthDeg(value) {
  if (!Number.isFinite(value)) return 0
  return ((value % 360) + 360) % 360
}

function destinationPoint(lat, lng, bearingDeg, distanceMeters) {
  const angularDistance = distanceMeters / EARTH_RADIUS_METERS
  const bearingRad = (bearingDeg * Math.PI) / 180
  const latRad = (lat * Math.PI) / 180
  const lngRad = (lng * Math.PI) / 180

  const sinLat1 = Math.sin(latRad)
  const cosLat1 = Math.cos(latRad)
  const sinDist = Math.sin(angularDistance)
  const cosDist = Math.cos(angularDistance)

  const lat2 = Math.asin(
    sinLat1 * cosDist + cosLat1 * sinDist * Math.cos(bearingRad)
  )
  const lng2 = lngRad + Math.atan2(
    Math.sin(bearingRad) * sinDist * cosLat1,
    cosDist - sinLat1 * Math.sin(lat2)
  )

  const normalizedLng = ((lng2 * 180) / Math.PI + 540) % 360 - 180
  return { lat: (lat2 * 180) / Math.PI, lng: normalizedLng }
}

function buildTileListAroundPoint({ lat, lng, zoom, radiusMeters }) {
  const latDelta = radiusMeters / 111320
  const safeCos = Math.max(Math.cos((lat * Math.PI) / 180), 0.2)
  const lngDelta = radiusMeters / (111320 * safeCos)
  const north = clamp(lat + latDelta, -85, 85)
  const south = clamp(lat - latDelta, -85, 85)
  const west = clamp(lng - lngDelta, -180, 180)
  const east = clamp(lng + lngDelta, -180, 180)

  const nwTile = latLngToTile(north, west, zoom)
  const seTile = latLngToTile(south, east, zoom)

  const minX = Math.floor(Math.min(nwTile.x, seTile.x))
  const maxX = Math.floor(Math.max(nwTile.x, seTile.x))
  const minY = Math.floor(Math.min(nwTile.y, seTile.y))
  const maxY = Math.floor(Math.max(nwTile.y, seTile.y))

  const tiles = []
  for (let x = minX; x <= maxX; x += 1) {
    for (let y = minY; y <= maxY; y += 1) {
      tiles.push({ x, y, z: zoom })
    }
  }
  return tiles
}

async function loadTerrainTilesBatched({ tiles, key, batchSize = 12 }) {
  const loadedEntries = []
  let failed = 0

  for (let index = 0; index < tiles.length; index += batchSize) {
    const batch = tiles.slice(index, index + batchSize)
    const results = await Promise.allSettled(
      batch.map(async (tile) => {
        const tileData = await loadTerrainTile({ ...tile, key })
        return { key: `${tile.z}/${tile.x}/${tile.y}`, data: tileData }
      })
    )
    for (const result of results) {
      if (result.status === 'fulfilled') {
        loadedEntries.push(result.value)
      } else {
        failed += 1
      }
    }
  }

  return { loadedEntries, failed }
}

async function ensureTerrainCoverageForPoint({
  point,
  key,
  zoom,
  radiusMeters,
  tileCache
}) {
  const requiredTiles = buildTileListAroundPoint({
    lat: point.lat,
    lng: point.lng,
    zoom,
    radiusMeters
  })
  const missingTiles = requiredTiles.filter((tile) => !tileCache.has(`${zoom}/${tile.x}/${tile.y}`))
  if (missingTiles.length === 0) {
    return { cache: tileCache, loadedEntries: [] }
  }

  const { loadedEntries } = await loadTerrainTilesBatched({ tiles: missingTiles, key })
  if (!loadedEntries.length) {
    return { cache: tileCache, loadedEntries: [] }
  }
  return {
    cache: mergeTileCache(tileCache, loadedEntries),
    loadedEntries
  }
}

function buildTerrainHorizonProfile({
  point,
  tileCache,
  zoom,
  rayCount = TERRAIN_TIME_RAY_COUNT,
  maxDistanceMeters = TERRAIN_TIME_RADIUS_METERS,
  stepMeters = TERRAIN_TIME_STEP_METERS
}) {
  const originElevation = getElevationAt(point.lat, point.lng, tileCache, zoom)
  if (!Number.isFinite(originElevation)) return null

  const horizon = new Float32Array(rayCount)
  const bearingStep = 360 / rayCount
  const earthCurveFactor = 1 / (2 * EARTH_RADIUS_METERS)

  for (let ray = 0; ray < rayCount; ray += 1) {
    const bearing = ray * bearingStep
    let maxAngleDeg = 0

    for (let distance = stepMeters; distance <= maxDistanceMeters; distance += stepMeters) {
      const samplePoint = destinationPoint(point.lat, point.lng, bearing, distance)
      const sampleElevation = getElevationAt(samplePoint.lat, samplePoint.lng, tileCache, zoom)
      if (!Number.isFinite(sampleElevation)) continue

      const curvatureDrop = distance * distance * earthCurveFactor
      const relativeElevation = sampleElevation - originElevation - curvatureDrop
      const angleDeg = (Math.atan2(relativeElevation, distance) * 180) / Math.PI
      if (angleDeg > maxAngleDeg) {
        maxAngleDeg = angleDeg
      }
    }

    horizon[ray] = Math.max(0, maxAngleDeg)
  }

  return horizon
}

function getHorizonAtAzimuth(horizonProfile, azimuthDeg) {
  if (!horizonProfile || !horizonProfile.length) return 0
  const normalizedAzimuth = normalizeAzimuthDeg(azimuthDeg)
  const step = 360 / horizonProfile.length
  const exactIndex = normalizedAzimuth / step
  const lowIndex = Math.floor(exactIndex) % horizonProfile.length
  const highIndex = (lowIndex + 1) % horizonProfile.length
  const ratio = exactIndex - Math.floor(exactIndex)
  const lowValue = horizonProfile[lowIndex] || 0
  const highValue = horizonProfile[highIndex] || 0
  return lowValue + (highValue - lowValue) * ratio
}

function buildTerrainLightWindowData({ dateStr, point, horizonProfile }) {
  const clearance = new Float32Array(1440)

  for (let minute = 0; minute < 1440; minute += 1) {
    const sun = getSunPosition(toHonoluluDate(dateStr, minute), point.lat, point.lng)
    const terrainHorizonDeg = getHorizonAtAzimuth(horizonProfile, sun.azimuthDeg)
    clearance[minute] = sun.altitudeDeg - terrainHorizonDeg
  }

  const daylightCross = findThresholdCrossings(clearance, 0)
  const twilightCross = findThresholdCrossings(clearance, -6)
  const goldenEdgeCross = findThresholdCrossings(clearance, -4)
  const goldenPeakCross = findThresholdCrossings(clearance, 6)

  const sunriseMinutes = daylightCross.rise
  const sunsetMinutes = daylightCross.set

  const ranges = [
    {
      id: 'blue-am',
      label: 'Blue (AM)',
      start: twilightCross.rise,
      end: goldenEdgeCross.rise,
      className: 'bg-sky-500/35'
    },
    {
      id: 'golden-am',
      label: 'Golden (AM)',
      start: goldenEdgeCross.rise,
      end: goldenPeakCross.rise,
      className: 'bg-amber-400/40'
    },
    {
      id: 'daytime',
      label: 'Daytime',
      start: goldenPeakCross.rise,
      end: goldenPeakCross.set,
      className: 'bg-yellow-200/45'
    },
    {
      id: 'golden-pm',
      label: 'Golden (PM)',
      start: goldenPeakCross.set,
      end: goldenEdgeCross.set,
      className: 'bg-amber-400/40'
    },
    {
      id: 'blue-pm',
      label: 'Blue (PM)',
      start: goldenEdgeCross.set,
      end: twilightCross.set,
      className: 'bg-sky-500/35'
    }
  ].filter((range) => Number.isFinite(range.start) && Number.isFinite(range.end) && range.end > range.start)

  const markers = [
    { id: 'sunrise', label: 'Sunrise', minute: sunriseMinutes, className: 'bg-rose-300' },
    { id: 'sunset', label: 'Sunset', minute: sunsetMinutes, className: 'bg-orange-300' }
  ]

  return {
    sunriseMinutes,
    sunsetMinutes,
    goldenMorningStart: goldenEdgeCross.rise,
    goldenMorningEnd: goldenPeakCross.rise,
    goldenEveningStart: goldenPeakCross.set,
    goldenEveningEnd: goldenEdgeCross.set,
    blueMorningStart: twilightCross.rise,
    blueMorningEnd: goldenEdgeCross.rise,
    blueEveningStart: goldenEdgeCross.set,
    blueEveningEnd: twilightCross.set,
    ranges,
    markers
  }
}

function toLatLngValue(target) {
  if (!target) return null
  const lat = typeof target.lat === 'function' ? target.lat() : target.lat
  const lng = typeof target.lng === 'function' ? target.lng() : target.lng
  if (typeof lat !== 'number' || typeof lng !== 'number') return null
  return { lat, lng }
}

function pickRenderGridSize({ isMobile, mode, sunAltitudeDeg, overlayWidth, hasShadeWorker }) {
  const width = Number.isFinite(overlayWidth) && overlayWidth > 0 ? overlayWidth : 1024
  const qualityMode = mode || 'quality'

  let pxPerSample
  let minGrid
  let maxGrid

  if (qualityMode === 'interactive') {
    pxPerSample = isMobile ? 15 : 13
    minGrid = isMobile ? 80 : 96
    maxGrid = isMobile ? 144 : 208
  } else if (qualityMode === 'preview') {
    pxPerSample = isMobile ? 11 : 9
    minGrid = isMobile ? 96 : 128
    maxGrid = isMobile ? 176 : 280
  } else {
    pxPerSample = isMobile ? 6 : 4
    minGrid = isMobile ? 128 : 192
    maxGrid = hasShadeWorker
      ? isMobile
        ? 256
        : 448
      : isMobile
        ? 192
        : 320
  }

  let target = clamp(Math.round(width / pxPerSample), minGrid, maxGrid)

  // Long shadows are expensive, so cap max detail a bit at very low sun.
  if (qualityMode === 'quality' && sunAltitudeDeg < 10) {
    target = Math.min(target, isMobile ? 208 : 352)
  }

  return target
}

export default function SunriseSunsetPlanner() {
  const mapRef = useRef(null)
  const overlayRef = useRef(null)
  const overlayStateRef = useRef(null)
  const shadeWorkerRef = useRef(null)
  const shadeJobIdRef = useRef(0)
  const shadeJobMetaRef = useRef(null)
  const lastSearchRef = useRef('')
  const hasUserInteractedRef = useRef(false)
  const ignoreNextZoomRef = useRef(false)
  const lastIdleAtRef = useRef(0)
  const lastTerrainUpdateRef = useRef(0)
  const terrainProfileCacheRef = useRef(new Map())

  const navigate = useNavigate()
  const location = useLocation()
  const {
    island: runtimeIsland,
    islandConfig,
    isApexHost
  } = useMemo(() => getRuntimeSiteContext(), [])
  const params = useMemo(() => new URLSearchParams(location.search), [location.search])

  const initialDate = parseDateParam(params.get('date')) || todayHonoluluDateString()
  const initialMinutes = parseTimeParam(params.get('time')) ?? parseMinutesFromDate(new Date())
  const initialPlaceId = params.get('placeId') || ''
  const initialLabel = params.get('label') || ''
  const debugMode = params.get('debug') === '1'
  const forceShade = params.get('forceShade') === '1'
  const initialIsMobile = isMobileViewport()
  const initialMapState = getInitialPlannerMapState({
    params,
    runtimeIsland,
    isApexHost,
    isMobile: initialIsMobile
  })

  const [island, setIsland] = useState(initialMapState.island)
  const [dateStr, setDateStr] = useState(initialDate)
  const [timeMinutes, setTimeMinutes] = useState(() => clampTimeToPlannerWindow(initialMinutes))
  const [mapView, setMapView] = useState(() => ({
    center: initialMapState.center,
    zoom: initialMapState.zoom
  }))
  const [compareA, setCompareA] = useState(null)
  const [compareB, setCompareB] = useState(null)
  const [mapType, setMapType] = useState('terrain')
  const [placeId] = useState(initialPlaceId)
  const [placeLabel] = useState(initialLabel)
  const [mapInstance, setMapInstance] = useState(null)
  const [isMobile, setIsMobile] = useState(initialIsMobile)
  const [isOverview, setIsOverview] = useState(initialMapState.isOverview)
  const [isHelpOpen, setIsHelpOpen] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [playbackSpeed, setPlaybackSpeed] = useState(4)
  const [isScrubbing, setIsScrubbing] = useState(false)
  const [isMapInteracting, setIsMapInteracting] = useState(false)
  const [renderGridSize, setRenderGridSize] = useState(initialIsMobile ? 96 : 128)
  const [terrainStatus, setTerrainStatus] = useState('idle')
  const [tileCache, setTileCache] = useState(() => new Map())
  const tileCacheRef = useRef(tileCache)
  const [shadeStatus, setShadeStatus] = useState('idle')
  const [activeDemZoom, setActiveDemZoom] = useState(DEM_ZOOM)
  const [error, setError] = useState(null)
  const [tileProbeStatus, setTileProbeStatus] = useState('idle')
  const [tileStats, setTileStats] = useState({ total: 0, missing: 0 })
  const [overlaySize, setOverlaySize] = useState({ width: 0, height: 0 })
  const [boundsStatus, setBoundsStatus] = useState('unknown')
  const [overlayOffset, setOverlayOffset] = useState({ x: 0, y: 0 })
  const [overlayClientRect, setOverlayClientRect] = useState(null)
  const [shadeStats, setShadeStats] = useState({ min: 0, max: 0, avg: 0 })
  const [perfStats, setPerfStats] = useState({ terrainMs: null, shadeMs: null, cacheHitRate: 0 })
  const [hasShadeWorker, setHasShadeWorker] = useState(false)
  const [activeTileBudget, setActiveTileBudget] = useState(0)
  const [terrainTimesBySlot, setTerrainTimesBySlot] = useState({
    selected: null,
    compareA: null,
    compareB: null
  })
  const [terrainTimesStatus, setTerrainTimesStatus] = useState({
    selected: 'idle',
    compareA: 'idle',
    compareB: 'idle'
  })
  const [terrainTimesError, setTerrainTimesError] = useState(null)

  const maptilerKey = process.env.REACT_APP_MAPTILER_KEY

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: MAP_LIBRARIES,
    mapIds: [process.env.REACT_APP_MAP_ID]
  })

  const center = useMemo(() => toIslandCenter(island), [island])
  const selectedTarget = useMemo(
    () => toLatLngValue(mapRef.current?.getCenter?.()) || mapView.center || center,
    [mapView, center]
  )
  const selectedIsland = useMemo(
    () => getIslandForLatLng(selectedTarget.lat, selectedTarget.lng),
    [selectedTarget]
  )

  const todayDateParts = useMemo(() => parseDateParts(todayHonoluluDateString()), [])
  const selectedDateParts = useMemo(() => parseDateParts(dateStr), [dateStr])
  const selectedYear = selectedDateParts?.year || todayDateParts?.year || new Date().getUTCFullYear()
  const daysInSelectedYear = getDaysInYear(selectedYear)
  const parsedDateDayOfYear = getDayOfYear(dateStr)
  const dateTimelineValue = clamp(
    Number.isFinite(parsedDateDayOfYear) ? parsedDateDayOfYear : 0,
    0,
    daysInSelectedYear - 1
  )
  const monthStops = useMemo(() => getMonthStopsForYear(selectedYear), [selectedYear])
  const dateTimelinePercent = useMemo(() => {
    const denominator = Math.max(1, daysInSelectedYear - 1)
    return clamp((dateTimelineValue / denominator) * 100, 0, 100)
  }, [dateTimelineValue, daysInSelectedYear])
  const selectedDateLabel = useMemo(() => formatPlannerDateLabel(dateStr), [dateStr])
  const yearOptions = useMemo(() => {
    const anchor = selectedYear || todayDateParts?.year || new Date().getUTCFullYear()
    return Array.from({ length: 9 }, (_, idx) => anchor - 4 + idx)
  }, [selectedYear, todayDateParts?.year])

  const selectedDate = useMemo(() => toHonoluluDate(dateStr, timeMinutes), [dateStr, timeMinutes])

  const sunPosition = useMemo(() => {
    return getSunPosition(selectedDate, selectedTarget.lat, selectedTarget.lng)
  }, [selectedDate, selectedTarget])

  const selectedTerrainPoint = useMemo(
    () => ({ lat: selectedTarget.lat, lng: selectedTarget.lng }),
    [selectedTarget.lat, selectedTarget.lng]
  )
  const compareATerrainPoint = useMemo(
    () => (compareA ? { lat: compareA.lat, lng: compareA.lng } : null),
    [compareA]
  )
  const compareBTerrainPoint = useMemo(
    () => (compareB ? { lat: compareB.lat, lng: compareB.lng } : null),
    [compareB]
  )

  const selectedTerrainTimes = terrainTimesBySlot.selected
  const compareATerrainTimes = terrainTimesBySlot.compareA
  const compareBTerrainTimes = terrainTimesBySlot.compareB

  const lightWindowData = useMemo(() => (
    selectedTerrainTimes || {
      sunriseMinutes: null,
      sunsetMinutes: null,
      goldenMorningStart: null,
      goldenMorningEnd: null,
      goldenEveningStart: null,
      goldenEveningEnd: null,
      blueMorningStart: null,
      blueMorningEnd: null,
      blueEveningStart: null,
      blueEveningEnd: null,
      ranges: [],
      markers: []
    }
  ), [selectedTerrainTimes])
  const timePresets = useMemo(() => {
    const sunriseMinute = Number.isFinite(lightWindowData.sunriseMinutes)
      ? Math.round(lightWindowData.sunriseMinutes)
      : null
    const sunsetMinute = Number.isFinite(lightWindowData.sunsetMinutes)
      ? Math.round(lightWindowData.sunsetMinutes)
      : null
    const middayMinute = Number.isFinite(sunriseMinute) && Number.isFinite(sunsetMinute)
      ? Math.round((sunriseMinute + sunsetMinute) / 2)
      : 12 * 60

    return [
      {
        id: 'sunrise',
        label: 'Sunrise',
        minute: sunriseMinute,
        disabled: !Number.isFinite(sunriseMinute)
      },
      {
        id: 'midday',
        label: 'Midday',
        minute: middayMinute,
        disabled: false
      },
      {
        id: 'sunset',
        label: 'Sunset',
        minute: sunsetMinute,
        disabled: !Number.isFinite(sunsetMinute)
      }
    ]
  }, [lightWindowData.sunriseMinutes, lightWindowData.sunsetMinutes])
  const visibleLightRanges = useMemo(() => (
    lightWindowData.ranges
      .map((range) => {
        const clipped = clipRangeToPlannerWindow(range.start, range.end)
        if (!clipped) return null
        return { ...range, ...clipped }
      })
      .filter(Boolean)
  ), [lightWindowData.ranges])
  const visibleLightMarkers = useMemo(() => (
    lightWindowData.markers.filter(
      (marker) =>
        Number.isFinite(marker.minute) &&
        marker.minute >= TIME_WINDOW_START_MINUTES &&
        marker.minute <= TIME_WINDOW_END_MINUTES
    )
  ), [lightWindowData.markers])
  const visibleTimeMinutes = clampTimeToPlannerWindow(timeMinutes)
  const sunRayLength = useMemo(
    () => getSunRayLengthPx(sunPosition.altitudeDeg),
    [sunPosition.altitudeDeg]
  )
  const sunriseAzimuthDeg = useMemo(() => {
    if (!Number.isFinite(lightWindowData.sunriseMinutes)) return null
    return getSunPosition(
      toHonoluluDate(dateStr, Math.round(lightWindowData.sunriseMinutes)),
      selectedTarget.lat,
      selectedTarget.lng
    ).azimuthDeg
  }, [dateStr, selectedTarget.lat, selectedTarget.lng, lightWindowData.sunriseMinutes])
  const sunsetAzimuthDeg = useMemo(() => {
    if (!Number.isFinite(lightWindowData.sunsetMinutes)) return null
    return getSunPosition(
      toHonoluluDate(dateStr, Math.round(lightWindowData.sunsetMinutes)),
      selectedTarget.lat,
      selectedTarget.lng
    ).azimuthDeg
  }, [dateStr, selectedTarget.lat, selectedTarget.lng, lightWindowData.sunsetMinutes])

  const compareData = useMemo(() => {
    if (!compareA || !compareB || !compareATerrainTimes || !compareBTerrainTimes) return null
    const sunriseA = compareATerrainTimes.sunriseMinutes
    const sunriseB = compareBTerrainTimes.sunriseMinutes
    const sunsetA = compareATerrainTimes.sunsetMinutes
    const sunsetB = compareBTerrainTimes.sunsetMinutes
    if (
      !Number.isFinite(sunriseA) ||
      !Number.isFinite(sunriseB) ||
      !Number.isFinite(sunsetA) ||
      !Number.isFinite(sunsetB)
    ) {
      return null
    }

    return {
      sunriseA,
      sunriseB,
      sunsetA,
      sunsetB,
      sunriseDelta: sunriseB - sunriseA,
      sunsetDelta: sunsetB - sunsetA
    }
  }, [compareA, compareB, compareATerrainTimes, compareBTerrainTimes])

  useEffect(() => {
    if (!isPlaying) return
    const handle = setInterval(() => {
      setTimeMinutes((prev) => advanceTimeInPlannerWindow(prev, playbackSpeed))
    }, PLAYBACK_INTERVAL_MS)
    return () => clearInterval(handle)
  }, [isPlaying, playbackSpeed])

  useEffect(() => {
    if (selectedDateParts) return
    setDateStr(todayHonoluluDateString())
  }, [selectedDateParts])

  useEffect(() => {
    tileCacheRef.current = tileCache
  }, [tileCache])

  useEffect(() => {
    let cancelled = false

    const slots = [
      { id: 'selected', point: selectedTerrainPoint },
      { id: 'compareA', point: compareATerrainPoint },
      { id: 'compareB', point: compareBTerrainPoint }
    ]

    const run = async () => {
      const nextData = {
        selected: null,
        compareA: null,
        compareB: null
      }
      const nextStatus = {
        selected: 'idle',
        compareA: 'idle',
        compareB: 'idle'
      }

      slots.forEach(({ id, point }) => {
        if (point) nextStatus[id] = maptilerKey ? 'loading' : 'error'
      })

      setTerrainTimesStatus(nextStatus)
      setTerrainTimesError(null)

      if (!maptilerKey) {
        if (!cancelled) {
          setTerrainTimesBySlot(nextData)
          setTerrainTimesError('Terrain-adjusted times require a MapTiler key.')
        }
        return
      }

      let workingCache = tileCacheRef.current
      const loadedEntriesForTimes = []
      const slotErrors = []

      for (const { id, point } of slots) {
        if (!point) continue
        try {
          const coverage = await ensureTerrainCoverageForPoint({
            point,
            key: maptilerKey,
            zoom: DEM_ZOOM,
            radiusMeters: TERRAIN_TIME_RADIUS_METERS,
            tileCache: workingCache
          })
          workingCache = coverage.cache
          if (coverage.loadedEntries.length) {
            loadedEntriesForTimes.push(...coverage.loadedEntries)
          }

          const profileKey = getTerrainProfileKey(point, DEM_ZOOM)
          let horizonProfile = terrainProfileCacheRef.current.get(profileKey)
          if (!horizonProfile) {
            horizonProfile = buildTerrainHorizonProfile({
              point,
              tileCache: workingCache,
              zoom: DEM_ZOOM
            })
            if (!horizonProfile) {
              throw new Error('terrain-horizon-unavailable')
            }
            terrainProfileCacheRef.current.set(profileKey, horizonProfile)
          }

          nextData[id] = buildTerrainLightWindowData({
            dateStr,
            point,
            horizonProfile
          })
          nextStatus[id] = 'ready'
        } catch {
          nextStatus[id] = 'error'
          slotErrors.push(id)
        }
      }

      if (cancelled) return

      if (loadedEntriesForTimes.length) {
        setTileCache((prev) => {
          const merged = mergeTileCache(prev, loadedEntriesForTimes)
          tileCacheRef.current = merged
          return merged
        })
      }

      setTerrainTimesBySlot(nextData)
      setTerrainTimesStatus(nextStatus)
      if (slotErrors.length) {
        setTerrainTimesError(`Terrain-aware times failed for: ${slotErrors.join(', ')}`)
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [
    dateStr,
    maptilerKey,
    selectedTerrainPoint,
    compareATerrainPoint,
    compareBTerrainPoint
  ])

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(isMobileViewport())
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (!mapInstance) return undefined
    const div = mapInstance.getDiv?.()
    if (!div) return undefined

    const markInteracted = () => {
      hasUserInteractedRef.current = true
    }

    div.addEventListener('mousedown', markInteracted)
    div.addEventListener('touchstart', markInteracted, { passive: true })
    div.addEventListener('wheel', markInteracted, { passive: true })

    return () => {
      div.removeEventListener('mousedown', markInteracted)
      div.removeEventListener('touchstart', markInteracted)
      div.removeEventListener('wheel', markInteracted)
    }
  }, [mapInstance])

  useEffect(() => {
    const minZoom = isMobile ? MOBILE_MIN_ZOOM : DESKTOP_MIN_ZOOM
    setMapView((prev) => {
      if (!prev?.zoom || prev.zoom < minZoom) {
        return { ...prev, zoom: minZoom }
      }
      return prev
    })
  }, [isMobile])

  useEffect(() => {
    setTileCache(new Map())
    setTerrainStatus('idle')
    setShadeStatus('idle')
  }, [island])

  useEffect(() => {
    const isInteractive = isPlaying || isScrubbing || isMapInteracting
    if (isInteractive) {
      setRenderGridSize(
        pickRenderGridSize({
          isMobile,
          mode: 'interactive',
          sunAltitudeDeg: sunPosition.altitudeDeg,
          overlayWidth: overlaySize.width,
          hasShadeWorker
        })
      )
      return undefined
    }
    setRenderGridSize(
      pickRenderGridSize({
        isMobile,
        mode: 'preview',
        sunAltitudeDeg: sunPosition.altitudeDeg,
        overlayWidth: overlaySize.width,
        hasShadeWorker
      })
    )
    const handle = setTimeout(() => {
      setRenderGridSize(
        pickRenderGridSize({
          isMobile,
          mode: 'quality',
          sunAltitudeDeg: sunPosition.altitudeDeg,
          overlayWidth: overlaySize.width,
          hasShadeWorker
        })
      )
    }, 220)
    return () => clearTimeout(handle)
  }, [
    isPlaying,
    isScrubbing,
    isMapInteracting,
    timeMinutes,
    isMobile,
    sunPosition.altitudeDeg,
    overlaySize.width,
    hasShadeWorker
  ])

  useEffect(() => {
    if (!mapInstance || !window.google?.maps?.OverlayView) return
    if (overlayRef.current) return

    const overlay = new window.google.maps.OverlayView()
    overlay.onAdd = function onAdd() {
      const canvas = document.createElement('canvas')
      canvas.style.position = 'absolute'
      canvas.style.top = '0'
      canvas.style.left = '0'
      canvas.style.width = '100%'
      canvas.style.height = '100%'
      canvas.style.pointerEvents = 'none'
      canvas.style.zIndex = '9999'
      canvas.className = 'sunshade-overlay'

      const panes = this.getPanes()
      panes.floatPane.appendChild(canvas)
      overlayStateRef.current = {
        canvas,
        width: 0,
        height: 0,
        projection: null,
        originX: 0,
        originY: 0
      }
    }

    overlay.draw = function draw() {
      const projection = this.getProjection()
      const map = this.getMap()
      const mapDiv = map.getDiv()
      const rect = mapDiv.getBoundingClientRect()
      const canvas = overlayStateRef.current?.canvas
      if (!canvas) return

      let nextWidth = Math.round(rect.width)
      let nextHeight = Math.round(rect.height)
      let nextLeft = 0
      let nextTop = 0

      if (projection?.fromContainerPixelToLatLng && projection.fromLatLngToDivPixel) {
        const tlLatLng = projection.fromContainerPixelToLatLng(new window.google.maps.Point(0, 0))
        if (tlLatLng) {
          const tlDiv = projection.fromLatLngToDivPixel(tlLatLng)
          if (tlDiv) {
            nextLeft = Math.round(tlDiv.x)
            nextTop = Math.round(tlDiv.y)
          }
        }
      }

      const needsResize = canvas.width !== nextWidth || canvas.height !== nextHeight
      if (needsResize) {
        canvas.width = nextWidth
        canvas.height = nextHeight
        canvas.style.width = `${nextWidth}px`
        canvas.style.height = `${nextHeight}px`
      }
      canvas.style.left = `${nextLeft}px`
      canvas.style.top = `${nextTop}px`

      overlayStateRef.current = {
        canvas,
        width: nextWidth,
        height: nextHeight,
        projection,
        originX: nextLeft,
        originY: nextTop
      }
    }

    overlay.onRemove = function onRemove() {
      const canvas = overlayStateRef.current?.canvas
      if (canvas?.parentNode) canvas.parentNode.removeChild(canvas)
      overlayStateRef.current = null
    }

    overlay.setMap(mapInstance)
    overlayRef.current = overlay

    return () => {
      overlay.setMap(null)
      overlayRef.current = null
    }
  }, [isLoaded, mapInstance])

  useEffect(() => {
    if (typeof Worker === 'undefined') {
      setHasShadeWorker(false)
      return undefined
    }

    let worker
    try {
      worker = new Worker(new URL('../../workers/shadeWorker.js', import.meta.url))
    } catch {
      setHasShadeWorker(false)
      return undefined
    }

    shadeWorkerRef.current = worker
    setHasShadeWorker(true)

    worker.onmessage = (event) => {
      const { id, shadeBuffer, error: workerError } = event.data || {}
      const pending = shadeJobMetaRef.current
      if (!pending || pending.id !== id) return
      shadeJobMetaRef.current = null

      if (workerError || !shadeBuffer) {
        setShadeStatus('error')
        setError('Unable to render shade overlay.')
        return
      }

      try {
        const shade = new Uint8ClampedArray(shadeBuffer)
        const finalShade = pending.shouldBlur ? blurShade(shade, pending.gridSize) : shade
        if (pending.debugMode) {
          let min = 255
          let max = 0
          let sum = 0
          for (let i = 0; i < finalShade.length; i += 1) {
            const value = finalShade[i]
            if (value < min) min = value
            if (value > max) max = value
            sum += value
          }
          setShadeStats({
            min,
            max,
            avg: Math.round(sum / finalShade.length)
          })
        }
        drawShade({
          canvas: pending.canvas,
          width: pending.width,
          height: pending.height,
          shade: finalShade,
          gridSize: pending.gridSize
        })
        setShadeStatus('ready')
        const shadeMs = Math.round(
          (typeof performance !== 'undefined' ? performance.now() : Date.now()) - pending.startedAt
        )
        setPerfStats((prev) => ({ ...prev, shadeMs }))
      } catch {
        setShadeStatus('error')
        setError('Unable to render shade overlay.')
      }
    }

    worker.onerror = () => {
      setHasShadeWorker(false)
      setShadeStatus('error')
      setError('Shade worker failed.')
    }

    return () => {
      shadeWorkerRef.current = null
      shadeJobMetaRef.current = null
      setHasShadeWorker(false)
      worker.terminate()
    }
  }, [])

  const syncOverlayState = () => {
    const map = mapRef.current
    const overlay = overlayRef.current
    const canvas = overlayStateRef.current?.canvas
    if (!map || !overlay || !canvas) return
    const rect = map.getDiv()?.getBoundingClientRect?.()
    if (!rect || !rect.width || !rect.height) return
    const overlayProjection = overlay.getProjection?.()
    if (!overlayProjection) return

    let nextWidth = Math.round(rect.width)
    let nextHeight = Math.round(rect.height)
    let nextLeft = 0
    let nextTop = 0

    if (overlayProjection?.fromContainerPixelToLatLng && overlayProjection.fromLatLngToDivPixel) {
      const tlLatLng = overlayProjection.fromContainerPixelToLatLng(
        new window.google.maps.Point(0, 0)
      )
      if (tlLatLng) {
        const tlDiv = overlayProjection.fromLatLngToDivPixel(tlLatLng)
        if (tlDiv) {
          nextLeft = Math.round(tlDiv.x)
          nextTop = Math.round(tlDiv.y)
        }
      }
    }
    if (canvas.width !== nextWidth || canvas.height !== nextHeight) {
      canvas.width = nextWidth
      canvas.height = nextHeight
      canvas.style.width = `${nextWidth}px`
      canvas.style.height = `${nextHeight}px`
    }
    canvas.style.left = `${nextLeft}px`
    canvas.style.top = `${nextTop}px`
    canvas.style.opacity = '1'
    canvas.style.outline = debugMode ? '2px solid rgba(255,0,0,0.75)' : ''
    overlayStateRef.current = {
      canvas,
      width: nextWidth,
      height: nextHeight,
      projection: overlayProjection,
      originX: nextLeft,
      originY: nextTop
    }
    setOverlaySize({ width: nextWidth, height: nextHeight })
    setOverlayOffset({ x: nextLeft, y: nextTop })
    if (debugMode) {
      const rect = canvas.getBoundingClientRect?.()
      if (rect) {
        setOverlayClientRect({
          left: Math.round(rect.left),
          top: Math.round(rect.top),
          width: Math.round(rect.width),
          height: Math.round(rect.height)
        })
      }
    }
  }

  const requestTerrainUpdate = useCallback(() => {
    if (!maptilerKey || !mapRef.current) return
    const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now()
    const now = Date.now()
    if (now - lastTerrainUpdateRef.current < 120) return
    lastTerrainUpdateRef.current = now

    const bounds = mapRef.current.getBounds?.()
    const nextCenter = toLatLngValue(mapRef.current.getCenter?.())
    const nextZoom = mapRef.current.getZoom?.()
    const minZoom = isMobile ? MOBILE_MIN_ZOOM : DESKTOP_MIN_ZOOM
    const isInteractive = isPlaying || isScrubbing || isMapInteracting
    const tileBudgetGroup = isMobile ? TILE_BUDGETS.mobile : TILE_BUDGETS.desktop
    const maxTiles = hasShadeWorker && !isInteractive
      ? tileBudgetGroup.quality
      : tileBudgetGroup.interactive
    setActiveTileBudget(maxTiles)

    if (!bounds || !nextCenter || !isValidLatLng(nextCenter.lat, nextCenter.lng)) {
      setBoundsStatus('missing')
      setTileStats({ total: 0, missing: 0 })
      return
    }

    setBoundsStatus('ok')
    setTerrainStatus('loading')
    setError(null)

    if (typeof nextZoom === 'number' && nextZoom < minZoom) {
      ignoreNextZoomRef.current = true
      mapRef.current.setZoom(minZoom)
    }

    const { zoom: demZoom, tiles } = pickDemZoom(
      bounds,
      typeof nextZoom === 'number' ? nextZoom : minZoom,
      maxTiles
    )
    setActiveDemZoom(demZoom)
    setTileStats({ total: tiles.length, missing: 0 })

    const cached = tileCacheRef.current
    const missingTiles = tiles.filter((tile) => {
      const key = `${tile.z}/${tile.x}/${tile.y}`
      return !cached.has(key)
    })
    setTileStats({ total: tiles.length, missing: missingTiles.length })
    const cacheHitRate = tiles.length
      ? Math.round(((tiles.length - missingTiles.length) / tiles.length) * 100)
      : 100
    setPerfStats((prev) => ({ ...prev, cacheHitRate }))

    if (missingTiles.length === 0) {
      setTerrainStatus('ready')
      const terrainMs = Math.round(
        (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt
      )
      setPerfStats((prev) => ({ ...prev, terrainMs }))
      return
    }

    Promise.all(
      missingTiles.map((tile) =>
        loadTerrainTile({ ...tile, key: maptilerKey })
          .then((data) => ({ key: `${tile.z}/${tile.x}/${tile.y}`, data }))
          .catch(() => null)
      )
    )
      .then((results) => {
        const loaded = results.filter((result) => result?.data)
        if (!loaded.length) {
          setTerrainStatus('error')
          setError('Unable to load terrain tiles. Check MapTiler key or network.')
          return
        }
        setTileCache((prev) => mergeTileCache(prev, loaded))
        setTerrainStatus('ready')
        const terrainMs = Math.round(
          (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt
        )
        setPerfStats((prev) => ({ ...prev, terrainMs }))
      })
      .catch(() => {
        setTerrainStatus('error')
        setError('Unable to load terrain tiles. Check MapTiler key or network.')
      })
  }, [maptilerKey, isMobile, isPlaying, isScrubbing, isMapInteracting, hasShadeWorker])

  useEffect(() => {
    if (!mapInstance || overlaySize.width === 0 || overlaySize.height === 0) return
    requestTerrainUpdate()
  }, [mapInstance, overlaySize.width, overlaySize.height, requestTerrainUpdate])

  const shareSearch = useMemo(() => {
    if (!mapView?.center) return ''
    const paramsNext = new URLSearchParams()
    paramsNext.set('date', dateStr)
    paramsNext.set('time', formatMinutes24(timeMinutes))
    paramsNext.set('lat', mapView.center.lat.toFixed(5))
    paramsNext.set('lng', mapView.center.lng.toFixed(5))
    paramsNext.set('z', String(mapView.zoom))
    if (island) paramsNext.set('island', island)
    if (placeId) paramsNext.set('placeId', placeId)
    if (placeLabel) paramsNext.set('label', placeLabel)
    if (debugMode) paramsNext.set('debug', '1')
    if (forceShade) paramsNext.set('forceShade', '1')
    return `?${paramsNext.toString()}`
  }, [dateStr, timeMinutes, mapView, island, placeId, placeLabel, debugMode, forceShade])

  useEffect(() => {
    if (!shareSearch) return
    if (shareSearch === lastSearchRef.current) return
    lastSearchRef.current = shareSearch
    navigate({ search: shareSearch }, { replace: true })
  }, [shareSearch, navigate])

  useEffect(() => {
    if (!overlayStateRef.current || tileCache.size === 0) return
    if (!mapRef.current) return

    const overlayState = overlayStateRef.current
    const projection = overlayState.projection
    if (!projection) return
    setOverlaySize({ width: overlayState.width, height: overlayState.height })

    setShadeStatus('rendering')
    const shadeStartedAt = typeof performance !== 'undefined' ? performance.now() : Date.now()
    const shouldBlur = !(isPlaying || isScrubbing || forceShade) && renderGridSize < 200
    const worker = shadeWorkerRef.current

    if (worker) {
      try {
        const sampled = sampleElevationGrid({
          projection,
          width: overlayState.width,
          height: overlayState.height,
          originX: overlayState.originX || 0,
          originY: overlayState.originY || 0,
          tileCache,
          zoom: activeDemZoom,
          gridSize: renderGridSize
        })
        const requestId = shadeJobIdRef.current + 1
        shadeJobIdRef.current = requestId
        shadeJobMetaRef.current = {
          id: requestId,
          canvas: overlayState.canvas,
          width: overlayState.width,
          height: overlayState.height,
          gridSize: renderGridSize,
          shouldBlur,
          debugMode,
          startedAt: shadeStartedAt
        }
        worker.postMessage({
          id: requestId,
          gridSize: renderGridSize,
          elevationGridBuffer: sampled.elevations.buffer,
          sunAltitude: sunPosition.altitudeDeg,
          sunAzimuth: sunPosition.azimuthDeg,
          dx: sampled.dx,
          dy: sampled.dy,
          forceShade
        }, [sampled.elevations.buffer])
        return
      } catch {
        shadeJobMetaRef.current = null
      }
    }

    shadeJobMetaRef.current = null
    try {
      const sampled = sampleElevationGrid({
        projection,
        width: overlayState.width,
        height: overlayState.height,
        originX: overlayState.originX || 0,
        originY: overlayState.originY || 0,
        tileCache,
        zoom: activeDemZoom,
        gridSize: renderGridSize
      })
      const shade = computeShadeFromElevationGrid({
        elevations: sampled.elevations,
        gridSize: renderGridSize,
        sunAltitude: sunPosition.altitudeDeg,
        sunAzimuth: sunPosition.azimuthDeg,
        dx: sampled.dx,
        dy: sampled.dy,
        forceShade
      })
      const finalShade = shouldBlur ? blurShade(shade, renderGridSize) : shade
      if (debugMode) {
        let min = 255
        let max = 0
        let sum = 0
        for (let i = 0; i < finalShade.length; i += 1) {
          const value = finalShade[i]
          if (value < min) min = value
          if (value > max) max = value
          sum += value
        }
        setShadeStats({
          min,
          max,
          avg: Math.round(sum / finalShade.length)
        })
      }
      drawShade({
        canvas: overlayState.canvas,
        width: overlayState.width,
        height: overlayState.height,
        shade: finalShade,
        gridSize: renderGridSize
      })
      setShadeStatus('ready')
      const shadeMs = Math.round(
        (typeof performance !== 'undefined' ? performance.now() : Date.now()) - shadeStartedAt
      )
      setPerfStats((prev) => ({ ...prev, shadeMs }))
    } catch (err) {
      setShadeStatus('error')
      setError('Unable to render shade overlay.')
    }
  }, [
    tileCache,
    sunPosition,
    timeMinutes,
    isPlaying,
    isScrubbing,
    renderGridSize,
    activeDemZoom,
    debugMode,
    forceShade,
    hasShadeWorker
  ])

  useEffect(() => {
    if (!debugMode || !maptilerKey) return
    const target = { lat: 21.3069, lng: -157.8583 }
    const { x, y } = latLngToTile(target.lat, target.lng, MIN_DEM_ZOOM)
    setTileProbeStatus('loading')
    loadTerrainTile({ x: Math.floor(x), y: Math.floor(y), z: MIN_DEM_ZOOM, key: maptilerKey })
      .then(() => setTileProbeStatus('ok'))
      .catch(() => setTileProbeStatus('error'))
  }, [debugMode, maptilerKey])

  const trackPlannerEvent = useCallback((eventName, params = {}) => {
    trackEvent(eventName, {
      tool: 'sunrise_sunset_planner',
      ...params
    })
  }, [])

  const beginScrub = useCallback(() => {
    setIsScrubbing(true)
  }, [])

  const endScrub = useCallback(() => {
    setIsScrubbing(false)
    trackPlannerEvent('sun_planner_time_scrub', { minute: timeMinutes })
  }, [timeMinutes, trackPlannerEvent])

  const captureComparePoint = useCallback((setter) => {
    setter({
      lat: selectedTarget.lat,
      lng: selectedTarget.lng,
      island: selectedIsland
    })
  }, [selectedTarget, selectedIsland])

  const focusComparePoint = useCallback((point) => {
    if (!point || !mapRef.current) return
    hasUserInteractedRef.current = true
    setIsOverview(false)
    const nextCenter = { lat: point.lat, lng: point.lng }
    mapRef.current.panTo(nextCenter)
    setMapView((prev) => ({ ...prev, center: nextCenter }))
    if (point.island) setIsland(point.island)
  }, [])

  const togglePlay = useCallback(() => {
    setIsPlaying((prev) => {
      const next = !prev
      trackPlannerEvent('sun_planner_play_toggle', { state: next ? 'play' : 'pause' })
      return next
    })
  }, [trackPlannerEvent])

  const changePlaybackSpeed = useCallback((nextSpeed) => {
    if (!PLAYBACK_SPEED_OPTIONS.includes(nextSpeed)) return
    setPlaybackSpeed((prev) => {
      if (prev === nextSpeed) return prev
      trackPlannerEvent('sun_planner_play_speed', { speed: nextSpeed })
      return nextSpeed
    })
  }, [trackPlannerEvent])

  const applyTimePreset = useCallback((preset) => {
    if (!preset || preset.disabled || !Number.isFinite(preset.minute)) return
    setIsPlaying(false)
    setTimeMinutes(clampTimeToPlannerWindow(preset.minute))
    trackPlannerEvent('sun_planner_time_preset', {
      preset: preset.id,
      minute: Math.round(preset.minute)
    })
  }, [trackPlannerEvent])

  const handleDateTimelineChange = useCallback((nextValue) => {
    setDateStr(dayOfYearToDateString(selectedYear, Number(nextValue)))
  }, [selectedYear])

  const handleDateInputChange = useCallback((nextDate) => {
    const parsed = parseDateParam(nextDate)
    if (!parsed) return
    setDateStr(parsed)
  }, [])

  const handleYearChange = useCallback((nextYearValue) => {
    const nextYear = Number(nextYearValue)
    if (!Number.isFinite(nextYear)) return
    setDateStr(dayOfYearToDateString(nextYear, dateTimelineValue))
  }, [dateTimelineValue])

  const nudgeDate = useCallback((deltaDays) => {
    setDateStr((prev) => shiftDateString(prev, deltaDays))
  }, [])

  if (loadError) return <div>Error loading map</div>

  const currentIslandLabel = ISLAND_LABELS[island] || islandConfig.title || 'Hawaiʻi'

  return (
    <div className="sunrise-sunset-planner-page pt-0">
      <Helmet>
        <title>{`${currentIslandLabel} Sunrise & Sunset Planner | Alaka'i Aloha`}</title>
        <meta
          name="description"
          content={`Plan sunrise and sunset moments on ${currentIslandLabel} with a live sun + mountain shade map.`}
        />
        <link rel="canonical" href={buildRuntimeCanonicalUrl('/sunrise-sunset')} />
      </Helmet>

      <div className="w-full px-4 sm:px-6 lg:px-8 space-y-6 pb-4">
        <div className="space-y-2">
          <h1 className="text-4xl font-bold">Sunrise & Sunset Planner</h1>
          <p className="text-sm sm:text-base text-white/70">
            Plan around actual sunrise and sunset with our terrain-aware shade map. Pick a date, move
            through the day, and pan the map to compare daylight across different spots.
          </p>
        </div>

        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-2">
            <div className="text-xs uppercase tracking-wide text-white/55">Time presets</div>
            <div className="flex flex-wrap gap-2">
              {timePresets.map((preset) => {
                const isActive =
                  Number.isFinite(preset.minute) &&
                  Math.abs(clampTimeToPlannerWindow(preset.minute) - visibleTimeMinutes) <= 2

                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => applyTimePreset(preset)}
                    disabled={preset.disabled}
                    title={
                      preset.disabled
                        ? 'Calculating terrain-aware time for the current bullseye location'
                        : `${preset.label}: ${formatMinutesRounded(preset.minute)}`
                    }
                    className={`px-3 py-1.5 rounded-full text-xs sm:text-sm border transition-colors disabled:opacity-45 disabled:cursor-not-allowed ${
                      isActive
                        ? 'bg-emerald-500/20 border-emerald-400/60 text-emerald-100'
                        : 'bg-white/5 border-white/20 text-white/75 hover:text-white hover:border-emerald-400/40'
                    }`}
                  >
                    <span>{preset.label}</span>
                    <span className="ml-1.5 text-white/55">
                      {Number.isFinite(preset.minute) ? formatMinutesRounded(preset.minute) : '...'}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          <div className="flex flex-wrap items-start gap-2 lg:justify-end">
            {[
              { id: 'terrain', label: 'Terrain' },
              { id: 'roadmap', label: 'Roadmap' },
              { id: 'satellite', label: 'Satellite' }
            ].map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  setMapType(item.id)
                  trackPlannerEvent('sun_planner_map_style_select', { map_style: item.id })
                }}
                className={`px-3 py-1 rounded-full text-xs sm:text-sm border transition-colors ${
                  mapType === item.id
                    ? 'bg-emerald-500/20 border-emerald-400/60 text-emerald-100'
                    : 'bg-white/5 border-white/20 text-white/70 hover:text-white hover:border-emerald-400/40'
                }`}
              >
                {item.label}
              </button>
            ))}
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setIsHelpOpen((prev) => !prev)
                  trackPlannerEvent('sun_planner_help_toggle')
                }}
                aria-expanded={isHelpOpen}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs sm:text-sm border border-white/20 bg-white/5 text-white/80 hover:text-white hover:border-emerald-400/40 transition-colors"
              >
                <HelpCircle className="h-3.5 w-3.5" aria-hidden="true" />
                Help
              </button>
              {isHelpOpen ? (
                <div className="absolute right-0 top-full mt-2 z-40 w-[min(22rem,calc(100vw-2rem))] rounded-lg border border-white/15 bg-slate-950/95 p-4 text-sm text-white/75 shadow-2xl">
                  <div className="flex items-start justify-between gap-3">
                    <div className="font-semibold text-white">How to use this map</div>
                    <button
                      type="button"
                      onClick={() => setIsHelpOpen(false)}
                      className="rounded-md p-1 text-white/60 hover:text-white hover:bg-white/10"
                      aria-label="Close help"
                    >
                      <X className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                  <ol className="mt-3 space-y-2 list-decimal pl-4">
                    <li>Drag the map until the bullseye sits on the exact spot you care about.</li>
                    <li>Change the date with the date slider or the date field.</li>
                    <li>Change time with the time slider, or press play to animate the day.</li>
                  </ol>
                  <div className="mt-3 border-t border-white/10 pt-3 text-xs text-white/60">
                    Times and rays update for the bullseye location.
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>

      </div>

      <div className="w-full">
        <div className="relative rounded-lg overflow-hidden border border-white/10 shadow-xl bg-black/30 h-72 sm:h-[520px] lg:h-[560px]">
          <div className="absolute top-3 right-3 z-30 flex items-center gap-2">
            <div className="inline-flex rounded-lg border border-white/25 bg-black/55 backdrop-blur-sm overflow-hidden">
              {PLAYBACK_SPEED_OPTIONS.map((speed) => (
                <button
                  key={speed}
                  type="button"
                  onClick={() => changePlaybackSpeed(speed)}
                  className={`px-2 py-1 text-[11px] sm:text-xs border-r border-white/15 last:border-r-0 transition-colors ${
                    playbackSpeed === speed
                      ? 'bg-emerald-400/25 text-emerald-100'
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {speed}x
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={togglePlay}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs sm:text-sm border border-white/25 bg-black/55 text-white/90 hover:text-white hover:border-emerald-400/50 backdrop-blur-sm"
            >
              {isPlaying ? (
                <Pause className="h-3.5 w-3.5" aria-hidden="true" />
              ) : (
                <Play className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              {isPlaying ? 'Pause' : 'Play'}
            </button>
          </div>
          {!maptilerKey ? (
            <div className="h-full flex items-center justify-center text-red-200 px-6 text-center">
              Terrain shading is unavailable. Add `REACT_APP_MAPTILER_KEY` to continue.
            </div>
          ) : isLoaded ? (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={mapView.center || center}
              zoom={Math.max(
                mapView.zoom ?? DEFAULT_ZOOM,
                isMobile ? MOBILE_MIN_ZOOM : DESKTOP_MIN_ZOOM
              )}
              options={{
                ...mapOptions,
                mapTypeId: mapType,
                minZoom: isMobile ? MOBILE_MIN_ZOOM : DESKTOP_MIN_ZOOM
              }}
              onLoad={(map) => {
                mapRef.current = map
                setMapInstance(map)
                setIsMapInteracting(false)
                if (isOverview) {
                  const minZoom = isMobile ? MOBILE_MIN_ZOOM : DESKTOP_MIN_ZOOM
                  const overviewCenter = getHawaiiBoundsCenter()
                  ignoreNextZoomRef.current = true
                  map.setCenter(overviewCenter)
                  map.setZoom(minZoom)
                  setMapView({ center: overviewCenter, zoom: minZoom })
                } else {
                  const initialMapCenter = mapView.center || center
                  const initialMapZoom = Math.max(
                    mapView.zoom ?? getIslandZoom(island, isMobile),
                    isMobile ? MOBILE_MIN_ZOOM : DESKTOP_MIN_ZOOM
                  )
                  ignoreNextZoomRef.current = true
                  map.setCenter(initialMapCenter)
                  map.setZoom(initialMapZoom)
                  setMapView({ center: initialMapCenter, zoom: initialMapZoom })
                }
                setTimeout(() => {
                  syncOverlayState()
                }, 0)
              }}
              onDragEnd={() => {
                if (!hasUserInteractedRef.current) return
                setIsOverview(false)
              }}
              onDragStart={() => {
                if (!hasUserInteractedRef.current) return
                setIsMapInteracting(true)
              }}
              onZoomChanged={() => {
                if (ignoreNextZoomRef.current) {
                  ignoreNextZoomRef.current = false
                  return
                }
                if (!hasUserInteractedRef.current) return
                setIsMapInteracting(true)
                setIsOverview(false)
              }}
              onIdle={() => {
                if (!mapRef.current) return
                setIsMapInteracting(false)
                lastIdleAtRef.current = Date.now()
                syncOverlayState()
                const nextCenter = toLatLngValue(mapRef.current.getCenter?.())
                const nextZoom = mapRef.current.getZoom?.()
                const minZoom = isMobile ? MOBILE_MIN_ZOOM : DESKTOP_MIN_ZOOM

                const isBogusWorld =
                  nextCenter &&
                  Math.abs(nextCenter.lat) < 1e-6 &&
                  Math.abs(nextCenter.lng) < 1e-6 &&
                  nextZoom === 0

                if (!nextCenter || !isValidLatLng(nextCenter.lat, nextCenter.lng) || isBogusWorld) {
                  return
                }

                const freezeOverview = isOverview && !hasUserInteractedRef.current
                let forcedOverview = false

                if (freezeOverview) {
                  const overviewCenter = getHawaiiBoundsCenter()
                  const overviewZoom = minZoom
                  if (
                    Math.abs(nextCenter.lat - overviewCenter.lat) > 1e-6 ||
                    Math.abs(nextCenter.lng - overviewCenter.lng) > 1e-6 ||
                    nextZoom !== overviewZoom
                  ) {
                    ignoreNextZoomRef.current = true
                    mapRef.current.setCenter(overviewCenter)
                    mapRef.current.setZoom(overviewZoom)
                    setMapView({ center: overviewCenter, zoom: overviewZoom })
                    forcedOverview = true
                  }
                }

                if (!freezeOverview && nextCenter && typeof nextZoom === 'number') {
                  setMapView((prev) => {
                    if (
                      prev.center &&
                      Math.abs(prev.center.lat - nextCenter.lat) < 1e-6 &&
                      Math.abs(prev.center.lng - nextCenter.lng) < 1e-6 &&
                      prev.zoom === nextZoom
                    ) {
                      return prev
                    }
                    return { center: nextCenter, zoom: nextZoom }
                  })
                }

                if (!freezeOverview) {
                  const nextIsland = getIslandForLatLng(nextCenter.lat, nextCenter.lng)
                  if (nextIsland && nextIsland !== island) {
                    setIsland(nextIsland)
                  }
                }

                if (forcedOverview) {
                  requestTerrainUpdate()
                  return
                }
                requestTerrainUpdate()
              }}
              onTilesLoaded={() => {
                if (Date.now() - lastIdleAtRef.current < 300) return
                syncOverlayState()
                requestTerrainUpdate()
              }}
            >
              {compareA ? (
                <MarkerF
                  position={{ lat: compareA.lat, lng: compareA.lng }}
                  label={{ text: 'A', color: '#111827', fontWeight: '700' }}
                  title={`A · ${formatLatLng(compareA.lat, compareA.lng)}`}
                  zIndex={20}
                  onClick={() => focusComparePoint(compareA)}
                />
              ) : null}
              {compareB ? (
                <MarkerF
                  position={{ lat: compareB.lat, lng: compareB.lng }}
                  label={{ text: 'B', color: '#111827', fontWeight: '700' }}
                  title={`B · ${formatLatLng(compareB.lat, compareB.lng)}`}
                  zIndex={20}
                  onClick={() => focusComparePoint(compareB)}
                />
              ) : null}
            </GoogleMap>
          ) : (
            <div className="h-full flex items-center justify-center">Loading map…</div>
          )}
          <div className="pointer-events-none absolute left-3 top-3 z-30 max-w-[calc(100%-7rem)] rounded-lg border border-white/15 bg-black/55 px-3 py-2 text-xs text-white/80 shadow-lg backdrop-blur-sm sm:max-w-xs">
            <div className="font-semibold text-white">Bullseye = selected location</div>
            <div className="mt-0.5 text-white/60">
              Drag the map to place it on the exact spot for timing.
            </div>
          </div>
          <SunLineLegend className="pointer-events-none absolute left-3 top-24 z-30 hidden rounded-lg border border-white/15 bg-black/55 px-3 py-2 text-xs text-white/75 shadow-lg backdrop-blur-sm sm:block" />
          <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
            {Number.isFinite(sunriseAzimuthDeg) ? (
              <div
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-full origin-bottom"
                style={{
                  width: `${DIRECTION_RAY_WIDTH_PX}px`,
                  height: `${DIRECTION_RAY_LENGTH_PX}px`,
                  transform: `translate(-50%, -100%) rotate(${sunriseAzimuthDeg}deg)`
                }}
              >
                <div
                  className="h-full w-full"
                  style={{
                    backgroundColor: 'rgba(249, 115, 22, 0.98)',
                    boxShadow: '0 0 12px rgba(251, 146, 60, 0.95)'
                  }}
                />
                <div
                  className="absolute -top-1 left-1/2 -translate-x-1/2 border-l-[7px] border-r-[7px] border-b-[10px] border-l-transparent border-r-transparent"
                  style={{ borderBottomColor: 'rgba(249, 115, 22, 1)' }}
                />
              </div>
            ) : null}
            {Number.isFinite(sunsetAzimuthDeg) ? (
              <div
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-full origin-bottom"
                style={{
                  width: `${DIRECTION_RAY_WIDTH_PX}px`,
                  height: `${DIRECTION_RAY_LENGTH_PX}px`,
                  transform: `translate(-50%, -100%) rotate(${sunsetAzimuthDeg}deg)`
                }}
              >
                <div
                  className="h-full w-full"
                  style={{
                    backgroundColor: 'rgba(194, 65, 12, 0.98)',
                    boxShadow: '0 0 12px rgba(239, 68, 68, 0.9)'
                  }}
                />
                <div
                  className="absolute -top-1 left-1/2 -translate-x-1/2 border-l-[7px] border-r-[7px] border-b-[10px] border-l-transparent border-r-transparent"
                  style={{ borderBottomColor: 'rgba(194, 65, 12, 1)' }}
                />
              </div>
            ) : null}
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-full origin-bottom"
              style={{
                width: `${DIRECTION_RAY_WIDTH_PX}px`,
                height: `${sunRayLength}px`,
                transform: `translate(-50%, -100%) rotate(${sunPosition.azimuthDeg}deg)`
              }}
            >
              <div
                className="h-full w-full"
                style={{
                  backgroundColor: 'rgba(251, 191, 36, 0.98)',
                  boxShadow: '0 0 12px rgba(245, 158, 11, 0.95)'
                }}
              />
              <div
                className="absolute -top-1 left-1/2 -translate-x-1/2 border-l-[7px] border-r-[7px] border-b-[10px] border-l-transparent border-r-transparent"
                style={{ borderBottomColor: 'rgba(251, 191, 36, 1)' }}
              />
            </div>
            <div className="relative h-8 w-8 rounded-full border border-white/80 shadow-[0_0_0_1px_rgba(0,0,0,0.35)]">
              <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-white/80" />
              <div className="absolute top-1/2 left-0 w-full h-px -translate-y-1/2 bg-white/80" />
            </div>
          </div>
          <div className="px-4 py-2 text-xs text-white/60 bg-black/40">
            Terrain data © MapTiler. Map data © Google.
          </div>
        </div>
        <SunLineLegend className="mx-4 mt-3 rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-xs text-white/75 sm:hidden" />
      </div>

      <div className="w-full bg-white/5 backdrop-blur border border-white/10 rounded-lg overflow-hidden">
        <div className="px-4 sm:px-6 lg:px-8 py-4 space-y-4">
          <div>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              <div className="px-3 py-2 rounded-lg border border-white/15 bg-white/5 text-sm text-white/90">
                {selectedDateLabel}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => nudgeDate(-1)}
                  className="px-3 py-1 rounded-lg text-xs border border-white/20 text-white/80 hover:text-white hover:border-emerald-400/40"
                >
                  -1 day
                </button>
                <button
                  type="button"
                  onClick={() => nudgeDate(1)}
                  className="px-3 py-1 rounded-lg text-xs border border-white/20 text-white/80 hover:text-white hover:border-emerald-400/40"
                >
                  +1 day
                </button>
                <select
                  value={selectedYear}
                  onChange={(e) => handleYearChange(e.target.value)}
                  className="px-2 py-1 rounded-lg text-xs border border-white/20 bg-white/5 text-white/85"
                  aria-label="Year"
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                <input
                  type="date"
                  value={dateStr}
                  onChange={(e) => handleDateInputChange(e.target.value)}
                  className="px-2 py-1 rounded-lg text-xs border border-white/20 bg-white/5 text-white/85"
                  aria-label="Trip date"
                />
              </div>
            </div>
            <div className="mt-3 relative h-10 rounded-lg bg-white/5 border border-white/10 overflow-hidden">
              {monthStops.map((stop) => {
                const left = (stop.dayIndex / Math.max(1, daysInSelectedYear - 1)) * 100
                return (
                  <div
                    key={`month-divider-${stop.label}`}
                    className="absolute top-0 h-full w-px bg-white/10"
                    style={{ left: `${left}%` }}
                  />
                )
              })}
              <input
                type="range"
                min="0"
                max={Math.max(0, daysInSelectedYear - 1)}
                step="1"
                value={dateTimelineValue}
                onChange={(e) => handleDateTimelineChange(e.target.value)}
                className="absolute inset-0 z-20 h-full w-full cursor-pointer opacity-0"
                aria-label="Date in year"
                aria-valuetext={selectedDateLabel}
              />
              <div
                className="absolute top-0 h-full z-10 pointer-events-none"
                style={{ left: `${dateTimelinePercent}%` }}
              >
                <div className="h-full w-[2px] bg-emerald-300" />
              </div>
            </div>
            <div className="relative h-4 mt-1 text-[11px] text-white/60">
              {monthStops.map((stop, index) => {
                const left = (stop.dayIndex / Math.max(1, daysInSelectedYear - 1)) * 100
                const isFirst = index === 0
                const isLast = index === monthStops.length - 1
                const transform = isFirst ? 'translateX(0)' : isLast ? 'translateX(-100%)' : 'translateX(-50%)'
                return (
                  <span
                    key={`month-label-${stop.label}`}
                    className="absolute top-0 whitespace-nowrap"
                    style={{ left: `${left}%`, transform }}
                  >
                    {stop.label}
                  </span>
                )
              })}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between text-xs uppercase tracking-wide text-white/60 gap-3">
              <span>Time</span>
              <span className="text-emerald-200 font-semibold normal-case">{formatMinutes(visibleTimeMinutes)}</span>
            </div>
            <div className="mt-3 relative h-10 rounded-lg bg-white/5 border border-white/10 overflow-hidden">
              {visibleLightRanges.map((range) => (
                <div
                  key={range.id}
                  className={`absolute top-0 h-full ${range.className}`}
                  style={{
                    left: `${minuteToPercent(range.start)}%`,
                    width: `${Math.max(0.75, minuteToPercent(range.end) - minuteToPercent(range.start))}%`
                  }}
                  title={`${range.label}: ${formatMinutesRounded(range.start)}–${formatMinutesRounded(range.end)}`}
                />
              ))}
              {visibleLightMarkers.map((marker) => (
                <div
                  key={marker.id}
                  className="absolute top-0 h-full"
                  style={{ left: `${minuteToPercent(marker.minute)}%` }}
                >
                  <div className={`h-full w-px ${marker.className}`} />
                  <div className="absolute top-0 -translate-x-1/2 -translate-y-[105%] text-[10px] text-white/75 whitespace-nowrap">
                    {marker.label}
                  </div>
                </div>
              ))}
              <input
                type="range"
                min={TIME_WINDOW_START_MINUTES}
                max={TIME_WINDOW_END_MINUTES}
                step="1"
                value={visibleTimeMinutes}
                onChange={(e) => setTimeMinutes(Number(e.target.value))}
                onMouseDown={beginScrub}
                onMouseUp={endScrub}
                onTouchStart={beginScrub}
                onTouchEnd={endScrub}
                onKeyUp={endScrub}
                aria-label="Time of day"
                aria-valuetext={formatMinutes(visibleTimeMinutes)}
                className="absolute inset-0 z-20 h-full w-full cursor-pointer opacity-0"
              />
              <div
                className="absolute top-0 h-full z-10 pointer-events-none"
                style={{ left: `${minuteToPercent(visibleTimeMinutes)}%` }}
              >
                <div className="h-full w-[2px] bg-emerald-300" />
              </div>
            </div>
            <div className="mt-1 flex items-center justify-between text-[11px] text-white/55">
              <span>{formatMinutes(TIME_WINDOW_START_MINUTES)}</span>
              <span>{formatMinutes(TIME_WINDOW_END_MINUTES)}</span>
            </div>

            <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-white/65">
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-amber-400/80" />
                Golden hour
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-yellow-300/80" />
                Daytime
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-sky-500/75" />
                Blue hour
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 sm:px-6 lg:px-8 space-y-4 pt-6 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white/5 border border-white/10 rounded-lg p-4 space-y-2">
            <div className="text-xs uppercase tracking-wide text-white/60">Sun Card</div>
            <div className="text-2xl font-semibold text-white">{formatHonoluluTime(selectedDate)}</div>
            <div className="text-sm text-white/70">Sun altitude: {sunPosition.altitudeDeg.toFixed(1)}°</div>
            <div className="text-sm text-white/70">Sun direction: {sunPosition.azimuthDeg.toFixed(0)}°</div>
            <div className="pt-2 text-sm text-white/60">
              Terrain sunrise: {formatMinutesRounded(lightWindowData.sunriseMinutes)} · Terrain sunset: {formatMinutesRounded(lightWindowData.sunsetMinutes)}
            </div>
            <div className="text-xs text-white/60">
              Golden hour (AM): {formatMinutesRounded(lightWindowData.goldenMorningStart)}–
              {formatMinutesRounded(lightWindowData.goldenMorningEnd)}
            </div>
            <div className="text-xs text-white/60">
              Golden hour (PM): {formatMinutesRounded(lightWindowData.goldenEveningStart)}–
              {formatMinutesRounded(lightWindowData.goldenEveningEnd)}
            </div>
            <div className="text-xs text-white/60">
              Blue hour (PM): {formatMinutesRounded(lightWindowData.blueEveningStart)}–
              {formatMinutesRounded(lightWindowData.blueEveningEnd)}
            </div>
            {terrainTimesStatus.selected === 'error' ? (
              <div className="text-xs text-red-200">Unable to compute terrain-aware time windows.</div>
            ) : null}
          </div>

          <div className="bg-white/5 border border-white/10 rounded-lg p-4 space-y-3">
            <div className="text-xs uppercase tracking-wide text-white/60">Compare Two Spots</div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  captureComparePoint(setCompareA)
                  trackPlannerEvent('sun_planner_compare_set', { slot: 'A' })
                }}
                className="px-3 py-2 rounded-lg text-xs sm:text-sm border border-white/20 text-white/80 hover:text-white hover:border-emerald-400/40"
              >
                Set A from center
              </button>
              <button
                type="button"
                onClick={() => {
                  captureComparePoint(setCompareB)
                  trackPlannerEvent('sun_planner_compare_set', { slot: 'B' })
                }}
                className="px-3 py-2 rounded-lg text-xs sm:text-sm border border-white/20 text-white/80 hover:text-white hover:border-emerald-400/40"
              >
                Set B from center
              </button>
              <button
                type="button"
                onClick={() => {
                  setCompareA(compareB)
                  setCompareB(compareA)
                  trackPlannerEvent('sun_planner_compare_swap')
                }}
                disabled={!compareA || !compareB}
                className="px-3 py-2 rounded-lg text-xs sm:text-sm border border-white/20 text-white/70 disabled:opacity-40"
              >
                Swap
              </button>
              <button
                type="button"
                onClick={() => {
                  setCompareA(null)
                  setCompareB(null)
                  trackPlannerEvent('sun_planner_compare_clear')
                }}
                disabled={!compareA && !compareB}
                className="px-3 py-2 rounded-lg text-xs sm:text-sm border border-white/20 text-white/70 disabled:opacity-40"
              >
                Clear
              </button>
            </div>

            <div className="text-xs text-white/70 space-y-1">
              <div>
                A: {compareA ? `${formatLatLng(compareA.lat, compareA.lng)} · ${ISLAND_LABELS[compareA.island] || '—'}` : 'Not set'}
              </div>
              <div>
                B: {compareB ? `${formatLatLng(compareB.lat, compareB.lng)} · ${ISLAND_LABELS[compareB.island] || '—'}` : 'Not set'}
              </div>
            </div>

            {compareData ? (
              <div className="text-sm text-white/75 space-y-1 pt-1">
                <div>
                  Sunrise A/B: {formatMinutesRounded(compareData.sunriseA)} / {formatMinutesRounded(compareData.sunriseB)}
                  {' '}<span className="text-white/55">({formatDeltaMinutes(compareData.sunriseDelta)})</span>
                </div>
                <div>
                  Sunset A/B: {formatMinutesRounded(compareData.sunsetA)} / {formatMinutesRounded(compareData.sunsetB)}
                  {' '}<span className="text-white/55">({formatDeltaMinutes(compareData.sunsetDelta)})</span>
                </div>
                <div className="text-xs text-emerald-200/90 pt-1">
                  {Math.round(compareData.sunsetDelta) === 0
                    ? 'A and B have the same sunset minute on this date.'
                    : `B sunset is ${formatDeltaMinutes(compareData.sunsetDelta)} than A.`}
                </div>
              </div>
            ) : terrainTimesStatus.compareA === 'loading' || terrainTimesStatus.compareB === 'loading' ? (
              <div className="text-xs text-amber-200">
                Calculating terrain-aware sunrise/sunset for A and B…
              </div>
            ) : terrainTimesStatus.compareA === 'error' || terrainTimesStatus.compareB === 'error' ? (
              <div className="text-xs text-red-200">
                Unable to compute terrain-aware compare times for one or both points.
              </div>
            ) : (
              <div className="text-xs text-white/55">
                Set A and B from the map center to compare sunrise/sunset timing.
              </div>
            )}
          </div>
        </div>

        {error ? (
          <div className="text-sm text-red-200 bg-red-500/10 border border-red-400/20 rounded-lg p-3">
            {error}
          </div>
        ) : null}
        {terrainTimesError ? (
          <div className="text-sm text-red-200 bg-red-500/10 border border-red-400/20 rounded-lg p-3">
            {terrainTimesError}
          </div>
        ) : null}
        {debugMode ? (
          <div className="text-xs text-white/70 bg-white/5 border border-white/10 rounded-lg p-3 space-y-1">
            <div>Debug: on</div>
            <div>Terrain status: {terrainStatus}</div>
            <div>Shade status: {shadeStatus}</div>
            <div>
              Terrain times: selected {terrainTimesStatus.selected} · A {terrainTimesStatus.compareA} · B {terrainTimesStatus.compareB}
            </div>
            <div>Shade pipeline: {hasShadeWorker ? 'worker' : 'main-thread fallback'}</div>
            <div>
              Render grid: {renderGridSize} · map interaction: {isMapInteracting ? 'active' : 'idle'}
            </div>
            <div>DEM zoom: {activeDemZoom}</div>
            <div>Tile budget: {activeTileBudget}</div>
            <div>
              Tiles: {tileStats.total} total / {tileStats.missing} missing · cache {tileCache.size}
            </div>
            <div>
              Cache hit rate: {perfStats.cacheHitRate}%
            </div>
            <div>
              Terrain load: {perfStats.terrainMs ?? '—'} ms · Shade render: {perfStats.shadeMs ?? '—'} ms
            </div>
            <div>
              Overlay: {overlaySize.width}×{overlaySize.height}
            </div>
            <div>
              Origin: {Math.round(overlayOffset.x)},{Math.round(overlayOffset.y)}
            </div>
            <div>
              Client rect:{' '}
              {overlayClientRect
                ? `${overlayClientRect.left},${overlayClientRect.top} ${overlayClientRect.width}×${overlayClientRect.height}`
                : 'unknown'}
            </div>
            <div>Bounds: {boundsStatus}</div>
            <div>
              Map view: {mapView.center?.lat.toFixed(4)},{' '}
              {mapView.center?.lng.toFixed(4)} z{mapView.zoom}
            </div>
            <div>
              Sun: {sunPosition.altitudeDeg.toFixed(1)}° / {sunPosition.azimuthDeg.toFixed(0)}°
            </div>
            <div>
              Shade: min {shadeStats.min} / max {shadeStats.max} / avg {shadeStats.avg}
            </div>
            <div>MapTiler probe: {tileProbeStatus}</div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
